/*
 * myPokemonPipSlots.ts
 *
 * Turns the current caught-Pokémon read into what the PiP HUD renders — the
 * My-Pokémon counterpart to `dexDetailPipSlots.ts` (which does the same for
 * the Pokédex SPECIES page).
 *
 * The facts shown are the ones an IV-checker user actually wants at a glance
 * (benchmarked against Poké Genie's overlay, 2026-08-20): the IV percent and
 * exact triple, CP / HP / DERIVED LEVEL, and the PvP league percents — not
 * just what one frame read, so the caller composes facts from the STORED
 * RECORD (which accumulates across views) overlaid by the live frame.
 *
 *   - a headline, e.g. `SLAKING #289` (one name, never nickname · species),
 *   - 16 grid slots (4x4, same native renderer). PACKED, not positional
 *     (2026-08-21 ask — the fixed slot map left blank columns and blank rows
 *     reserving panel room on a HUD that is all about glanceable density):
 *     each semantic row keeps its own line but compacts its facts left, and
 *     a row with nothing read yet gives its line up entirely, so the native
 *     renderer sizes the grid to the rows that actually carry facts:
 *
 *       IV 93%    15/14/13   3★       LUCKY      the appraisal row
 *       CP 4567   HP 182     LVL 49   ♂          identity + derived level
 *       GL 50.7%  UL 92.3%   SHINY               PvP percents + shiny
 *       Yawn      Play Ro…   Earthqu…            the moveset
 *
 *   - a top-1% league badge (`leagueBadge`) when the Pokémon ranks >= 99% in
 *     a league — the same rule as the list page's dot-rail league ball
 *     (`topLeagueBadge`), so the PiP celebrates exactly the Pokémon the list
 *     does. The native HUD draws the matching ball in its top-right corner.
 */
import { leagueByKey } from "./battleMeta";
import type { MyPokemonFrame } from "./myPokemonOcr";
import type { MyPokemonRecord } from "./myPokemonStore";
import { topLeagueBadge, type MyPokemonDerived } from "./myPokemonDerived";
import { bestPvpStanding } from "./myPokemonRecommendations";
import { bestPotentialFor, isLittleCupSpecies } from "./bestPotential";
import { baseStatsForRecord } from "./myPokemonDerived";
import { computeCp } from "./ivSolver";
import { leagueFullRank, rankPercentile } from "./pvpRank";
import { hudArtworkUrl, hudFormIdFor } from "./hudArtwork";
import { PIP_SLOT_COUNT } from "./dexDetailPipSlots";
import { moveTypeHex } from "./typeHex";

/**
 * The IV line before the appraisal: the same slot, size and shape as the
 * "12/14/15 · 91.1%" line it becomes, so the card does not re-flow when
 * the bars are read (2026-09-28).
 */
export const IV_PLACEHOLDER_LINE = "?/?/?";

/**
 * True when a nickname is just the species name wearing stat decorations, so
 * showing both would print the species twice. Compares on letters only: the
 * generator's styled digits flatten to ASCII digits through OCR, and GO's own
 * gender/size marks are non-letters too.
 */
export function nicknameRestatesSpecies(
  nickname: string | null,
  speciesName: string | null,
): boolean {
  if (!nickname || !speciesName) return false;
  const letters = (v: string) => v.replace(/[^A-Za-z]/g, "").toLowerCase();
  const nick = letters(nickname);
  const species = letters(speciesName);
  if (nick.length === 0 || species.length === 0) return false;
  return nick === species;
}

/** Everything the HUD needs, already merged from record + frame + derived. */
export interface MyPokemonHudFacts {
  speciesId: number | null;
  speciesName: string | null;
  nickname: string | null;
  displayName: string | null;
  cp: number | null;
  /**
   * Where the CP came from when the banner could not be read (2026-09-20):
   * "exact" when the max HP and IVs name one CP, "likely" when the HP fits
   * two half-levels (printed with a tilde), null when the CP was read.
   */
  cpFromHp: "exact" | "likely" | null;
  hpMax: number | null;
  ivAtk: number | null;
  ivDef: number | null;
  ivHp: number | null;
  stars: number | null;
  isLucky: boolean;
  /** Record-only statuses (the page's Shadow / Purified badges are not read live). */
  isShadow: boolean;
  isPurified: boolean;
  isShiny: boolean;
  /** Dynamax / Gigantamax (2026-09-28): GO's red energy glow behind the art. */
  isDynamax: boolean;
  isGmax: boolean;
  gender: "male" | "female" | null;
  /** The record's form (a costume can only evolve into the same costume), record-only. */
  formId: string | null;
  /** The trainer's locked destiny from the record page, record-only (2026-09-20). */
  pvpTarget: { formSpeciesId: number; league: "great" | "ultra" | "master" } | null;
  level: number | null;
  ivPercent: number | null;
  greatPct: number | null;
  ultraPct: number | null;
  masterPct: number | null;
  /** League-cap eligibility (false = CP already over the cap). */
  greatEligible: boolean;
  ultraEligible: boolean;
  moves: Array<{ name: string }>;
  /**
   * Which view the live frame came from (2026-09-22): the appraisal overlay
   * (IV bars up) or the detail page. The HUD's right-hand badge shows the
   * destiny picture while the bars are up and the moveset once the trainer
   * has moved off them.
   */
  view: "detail" | "appraisal";
}

/**
 * Compose HUD facts: the stored record carries everything earlier views
 * accumulated (the appraisal frame has no CP; the record remembers it), the
 * live frame fills anything the record lacks, derived stats ride alongside.
 */
export function myPokemonHudFacts(
  frame: MyPokemonFrame,
  record: MyPokemonRecord | null,
  derived: MyPokemonDerived | null,
  /** The CP worked out from HP and IVs when neither side read one (cpFromHp). */
  expectedCp?: { cp: number; exact: boolean } | null,
): MyPokemonHudFacts {
  const readCp = record != null && record.cp != null && !record.cpDerived ? record.cp : frame.cp;
  const cp = readCp ?? expectedCp?.cp ?? record?.cp ?? null;
  const cpFromHp: MyPokemonHudFacts["cpFromHp"] =
    readCp != null ? null : expectedCp ? (expectedCp.exact ? "exact" : "likely") : record?.cpDerived ? "likely" : null;
  return {
    speciesId: record?.speciesId ?? frame.speciesId,
    speciesName: record?.speciesName ?? frame.speciesName,
    nickname: record?.nickname ?? frame.nickname,
    displayName: frame.displayName,
    cp,
    cpFromHp,
    hpMax: record?.hpMax ?? frame.hpMax,
    ivAtk: record?.ivAtk ?? frame.iv?.atk ?? null,
    ivDef: record?.ivDef ?? frame.iv?.def ?? null,
    ivHp: record?.ivHp ?? frame.iv?.hp ?? null,
    stars: record?.appraisalStars ?? frame.appraisalStars,
    isLucky: (record?.isLucky ?? false) || frame.isLucky,
    isShadow: (record?.isShadow ?? false) || frame.isShadow,
    isPurified: record?.isPurified ?? false,
    isShiny: (record?.isShiny ?? false) || frame.isShiny,
    isDynamax: (record?.isDynamax ?? false) || (frame.isDynamax ?? false),
    isGmax: (record?.isGmax ?? false) || (frame.isGmax ?? false),
    gender: record?.gender ?? frame.gender,
    formId: record?.formId ?? null,
    pvpTarget: record?.pvpTarget ?? null,
    level: derived?.level ?? null,
    ivPercent: derived?.ivPercent ?? null,
    greatPct: derived?.greatPct ?? null,
    ultraPct: derived?.ultraPct ?? null,
    masterPct: derived?.masterPct ?? null,
    greatEligible: derived?.greatEligible ?? true,
    ultraEligible: derived?.ultraEligible ?? true,
    moves: (record?.moves.length ? record.moves : frame.moves).map((m) => ({
      name: m.name,
    })),
    view: frame.view,
  };
}

/**
 * "CP 1234" as read, or "CP ~1234" when the CP is the calculation from HP
 * and IVs and the HP fits two half-levels (2026-09-20). An exact
 * calculation prints plain: it IS the CP.
 */
export function cpLine(facts: Pick<MyPokemonHudFacts, "cp" | "cpFromHp">): string {
  return `CP ${facts.cpFromHp === "likely" ? "~" : ""}${facts.cp}`;
}

/**
 * Little League call-out (2026-10-12 owner ask): a scanned Pokémon whose IV
 * spread ranks in the top 1% of 4096 for the CP 500 cup, printed as a hero
 * line while the destiny picture stays exactly as it was. Little League is
 * cheap to build for and newer players enjoy it, so the HUD says so as they
 * scan. Top 1% = rank 41 or better ((41 - 1) / 4096 < 1%).
 */
export const LITTLE_LEAGUE_CALLOUT_MAX_RANK = 41;

export function littleLeagueCallout(
  facts: Pick<MyPokemonHudFacts, "speciesId" | "formId" | "gender" | "ivAtk" | "ivDef" | "ivHp" | "level" | "cp">,
): { rank: number; percentile: string; line: string } | null {
  if (facts.speciesId == null || facts.ivAtk == null || facts.ivDef == null || facts.ivHp == null) return null;
  // The Little Cup rule: a base stage that can still evolve, under CP 500.
  if (!isLittleCupSpecies(facts.speciesId)) return null;
  const base = baseStatsForRecord({ speciesId: facts.speciesId, formId: facts.formId, gender: facts.gender });
  if (!base) return null;
  const iv = { atk: facts.ivAtk, def: facts.ivDef, sta: facts.ivHp };
  // GO has no power-down: the Pokémon must already sit under the cap. The
  // derived level is the surer read; the printed CP stands in when the level
  // is unknown; neither known, the HUD cannot vouch and says nothing.
  const cpNow = facts.level != null ? computeCp(base, iv, facts.level) : facts.cp;
  if (cpNow == null || cpNow > 500) return null;
  const rank = leagueFullRank("little", { base, iv });
  if (rank == null || rank > LITTLE_LEAGUE_CALLOUT_MAX_RANK) return null;
  const percentile = rankPercentile(rank);
  return { rank, percentile, line: `LITTLE LEAGUE #${rank} · ${percentile.toUpperCase()}` };
}

export interface MyPokemonPipSlots {
  /** HUD headline: `SLAKING #289`, or the printed name when the species is unresolved. */
  headline: string | null;
  /** Exactly PIP_SLOT_COUNT entries; "" = unread/absent. */
  slots: string[];
  /** Top-1% league (>= 99% rank) for the native league-ball badge, or null. */
  leagueBadge: "great" | "ultra" | "master" | null;
  /**
   * HERO BAND (2026-08-26 ask): the league percents, drawn natively at a
   * LARGER size than the grid cells ("GL 51.2% · UL 92.3% · ML 88.9%").
   * Ineligible leagues (CP over the cap) are omitted; null while no percent
   * is known yet.
   */
  pvpLine: string | null;
  /**
   * Evolution-potential callout, brand green on the HUD: "EVOLVE → ARCANINE
   * · #1 GL" when the best claim needs an evolution, "TOP #3 GL PICK" when
   * the species already ranks as it stands. Null when nothing in the chain
   * makes the top-40 claim window, or when the TARGET VISUAL below carries
   * the same story in pictures.
   */
  evoLine: string | null;
  /**
   * THE TARGET VISUAL (2026-08-30 ask: "a better visual story than words").
   * What this exact IV triple could become: the best form's art with its
   * league's ball overlapping the sprite and an oversized rank beside it,
   * drawn natively. Needs the IVs, so null until the appraisal is read.
   * `name` is set only when the target is an EVOLUTION (the current species'
   * own art needs no caption).
   */
  target: {
    spriteUrl: string;
    /**
     * The league the standing is stated for, or null before the appraisal
     * for a species with no PvP standing (2026-09-28): the card then draws
     * the art alone, no ball and no rank. Until then such a Pokémon (a
     * Galarian Stunfisk, GL #71) had NO picture at all, only text.
     */
    league: "great" | "ultra" | "master" | null;
    /**
     * Every league sharing exactly this standing, GL-first (see
     * bestPotential). One ball is drawn per entry; the standing is stated
     * once. Contains `league` when there is one; empty when there is none.
     */
    leagues: Array<"great" | "ultra" | "master">;
    rank: number | null;
    name: string | null;
    /**
     * The percent-of-best the pick was MADE on, pre-formatted ("97.6%"), or
     * "" when unknown.
     *
     * 2026-09-01: the block used to show the rank alone, and that is what made
     * a correct pick look wrong. A 12/13/15 Snom's card read "GL 94.1% · UL
     * 94.1% · IV 88.9%" on the left and "FROSMOTH · ML #35" on the right, so
     * the only reading available was that the HUD had chosen the 88.9% league
     * over the 94.1% ones. The number it actually chose on — Frosmoth's 97.6%
     * as a Master mon — appeared nowhere. A claim has to show its own evidence.
     */
    percentText: string;
    /**
     * The in-game backdrop behind the art (2026-09-18 owner ask), the same
     * language SpriteAura draws on the web: shadow flames, the lucky ray
     * burst, the purified glow. Null for an ordinary Pokémon.
     */
    aura: "shadow" | "dynamax" | "lucky" | "purified" | null;
  } | null;
  /**
   * THE SPLIT LAYOUT'S LEFT COLUMN (2026-08-31 ask: "destiny image the right
   * 50%, less of the other information but larger on the left"). Compact
   * fact lines drawn natively under the headline, one per line, the LAST
   * entry at a larger size (the overall standing). Populated only alongside
   * `target` (the split layout needs both halves); the native HUD renders
   * the classic packed grid whenever this is empty. No HP, no repetition:
   *
   *   CP 736
   *   15/15/15
   *   GL 95.7% #12     (omitted when the league is ineligible)
   *   UL 100% #1
   *   IV 100% #1       (bigger: the overall percent + full species rank)
   */
  heroLines: string[];
  /** The Little League call-out behind the hero line, for the web preview and tests. */
  littleLeague: { rank: number; percentile: string } | null;
  /**
   * 2026-09-03: the moves the page read (fast move first), drawn as pills in
   * the split layout's left column. Empty without the target, when the
   * classic grid still carries them in its own row.
   */
  movePills: string[];
  /** Hex per pill, index for index with movePills; "" for an unknown move. */
  movePillColors: string[];
  /**
   * 2026-09-22 (Android badge): the moveset PvPoke ranks the TARGET form on
   * in the target league (fast move first), clipped like the pills so the
   * two lists compare as equals. Empty without a target or when the target
   * is not in that league's ranking. Same source as the record page's
   * "Best with ..." line (myPokemonRecommendations).
   */
  moveRecs: string[];
  /**
   * 2026-09-22: what the right-hand badge should show. "ivs" while the
   * appraisal bars are on screen (the destiny picture), "moves" once the
   * trainer has moved off them (registered moves against the recommended
   * ones).
   */
  hudFocus: "ivs" | "moves";
  /**
   * 2026-09-22: the raw IV percent, so the native HUD can colour its border
   * by GO's own appraisal bands the way the trainer just saw them. Null
   * until the IVs are read.
   */
  ivPercent: number | null;
}

/**
 * PvPoke's recommended moveset for a species in a league: the top-100
 * row when it has one, else the league's role-ranking metadata (a
 * Charizard is nowhere in the Ultra top-100 but ranks in a role there, and
 * that row carries the same moveset), else the first league that knows it.
 * Non-shadow rows only: this scanner cannot vouch for a shadow bonus.
 */
function rankedMovesFor(dexId: number, league: string): string[] {
  const inLeague = (key: string): string[] | null => {
    const l = leagueByKey(key);
    if (l.key !== key) return null;
    const row = l.entries.find((e) => e.dexId === dexId && !e.shadow);
    if (row) return row.moves;
    const sp = Object.values(l.species).find((e) => e.dexId === dexId && !e.shadow);
    return sp ? sp.moves : null;
  };
  const own = inLeague(league);
  if (own) return own;
  for (const key of ["great", "ultra", "master"]) {
    if (key === league) continue;
    const other = inLeague(key);
    if (other) return other;
  }
  return [];
}

/** "Great League" (BATTLE_META label) → the HUD's GL/UL/ML vocabulary. */
function shortLeague(label: string): string {
  if (/great/i.test(label)) return "GL";
  if (/ultra/i.test(label)) return "UL";
  if (/master/i.test(label)) return "ML";
  return label.toUpperCase();
}

/** Clip a move name to what a quarter-width PiP cell can show. */
function clipMove(name: string): string {
  return name.length > 9 ? `${name.slice(0, 8)}…` : name;
}

/**
 * A pill has more room than a grid cell: the longest GO move names fit
 * whole. 24, up from 16 (2026-09-22 Galaxy photos: the badge's moves face
 * was cutting names short, and "Weather Ball (Fire)" is 19).
 */
function clipPill(name: string): string {
  return name.length > 24 ? `${name.slice(0, 23)}…` : name;
}

/** One-decimal percent, trimming ".0" so full bars read clean ("100%"). */
function pct(v: number): string {
  const s = v.toFixed(1).replace(/\.0$/, "");
  return `${s}%`;
}

export function myPokemonPipSlots(facts: MyPokemonHudFacts): MyPokemonPipSlots {
  // The four semantic rows, each compacted left. An empty row is dropped so
  // the native grid only reserves height for lines that carry facts.
  const appraisal: string[] = [];
  if (facts.ivPercent != null) appraisal.push(`IV ${pct(facts.ivPercent)}`);
  if (facts.ivAtk != null && facts.ivDef != null && facts.ivHp != null) {
    appraisal.push(`${facts.ivAtk}/${facts.ivDef}/${facts.ivHp}`);
  }
  if (facts.stars != null) appraisal.push(`${facts.stars}★`);
  if (facts.isLucky) appraisal.push("LUCKY");

  // CP moved OFF the grid into the hero line (2026-08-30 ask: "the CP line
  // can be bigger, use the space") — see pvpLine below.
  const identity: string[] = [];
  if (facts.hpMax != null) identity.push(`HP ${facts.hpMax}`);
  if (facts.level != null) identity.push(`LVL ${facts.level}`);
  if (facts.gender != null) identity.push(facts.gender === "male" ? "♂" : "♀");

  // GL/UL moved OFF the grid to the hero line (2026-08-26 ask: the grid
  // font, dragged down by move names, undersold the league percents). The
  // row keeps the shiny chip.
  const pvp: string[] = [];
  if (facts.isShiny) pvp.push("SHINY");

  const moves = facts.moves.slice(0, 4).map((m) => clipMove(m.name));

  const rows = [appraisal, identity, pvp, moves].filter((r) => r.length > 0);
  const slots = new Array<string>(PIP_SLOT_COUNT).fill("");
  rows.forEach((row, ri) => {
    row.slice(0, 4).forEach((label, ci) => {
      slots[ri * 4 + ci] = label;
    });
  });

  const species =
    facts.speciesName != null && facts.speciesId != null
      ? `${facts.speciesName.toUpperCase()} #${facts.speciesId}`
      : null;
  // ONE name (2026-09-17 owner): the headline is the species, never
  // "nickname · SPECIES". The pairing used to print a misread of the species
  // as a nickname beside it ("Spriga: · SPRIGATITO #906") and even a real
  // nickname read as a double name. The nickname still lives on the record
  // and the detail page; up here the species is what the trainer is
  // looking at. No nickname at all up here (2026-09-18 owner): an
  // unresolved species leaves the headline empty and the status line says
  // what to do ("Appraise it so I can identify it"), rather than printing a
  // name the card cannot vouch for.
  const headline = species;
  // One backdrop, strongest story first: a Shadow's flames, then the
  // Dynamax / Gigantamax red energy (2026-09-28 owner ask), then the lucky
  // burst, then the purified glow.
  const aura: NonNullable<MyPokemonPipSlots["target"]>["aura"] = facts.isShadow
    ? "shadow"
    : facts.isDynamax || facts.isGmax
      ? "dynamax"
      : facts.isLucky
        ? "lucky"
        : facts.isPurified
          ? "purified"
          : null;

  const badge = topLeagueBadge({
    level: facts.level,
    ivPercent: facts.ivPercent,
    greatPct: facts.greatPct,
    ultraPct: facts.ultraPct,
    masterPct: facts.masterPct,
    greatEligible: facts.greatEligible,
    ultraEligible: facts.ultraEligible,
    bestLeagueRank: null,
  });

  // The hero line leads with CP at hero size (2026-08-30 ask), then the
  // league percents.
  const leagues: string[] = [];
  if (facts.cp != null) leagues.push(cpLine(facts));
  if (facts.greatEligible && facts.greatPct != null) leagues.push(`GL ${pct(facts.greatPct)}`);
  if (facts.ultraEligible && facts.ultraPct != null) leagues.push(`UL ${pct(facts.ultraPct)}`);
  if (facts.masterPct != null) leagues.push(`ML ${pct(facts.masterPct)}`);
  const pvpLine = leagues.length > 0 ? leagues.join(" · ") : null;

  // The target visual: this IV triple's best possible self across the whole
  // evolution line (lib/bestPotential). Shiny records show the shiny art of
  // what they become.
  let target: MyPokemonPipSlots["target"] = null;
  let targetSpeciesId: number | null = null;
  if (
    facts.speciesId != null &&
    facts.ivAtk != null &&
    facts.ivDef != null &&
    facts.ivHp != null
  ) {
    const potential = bestPotentialFor(
      {
        speciesId: facts.speciesId,
        ivAtk: facts.ivAtk,
        ivDef: facts.ivDef,
        ivHp: facts.ivHp,
        cp: facts.cp,
        // A costume only evolves into the same costume, and a gender-locked
        // stage needs the right gender (2026-09-20: a hat Floragato drawn as
        // Meowscarada; a female Litleo's destiny was the maned Pyroar).
        formId: facts.formId,
        gender: facts.gender,
        pvpTarget: facts.pvpTarget,
      },
      facts.level,
    );
    if (potential) {
      targetSpeciesId = potential.speciesId;
      target = {
        // The target's art in the record's FORM (2026-09-28): an Alolan
        // Marowak's own art, an Alolan Vulpix's Alolan Ninetales.
        spriteUrl: hudArtworkUrl(
          potential.speciesId,
          hudFormIdFor(facts.speciesId, facts.formId, potential.speciesId),
          // A Gigantamax individual draws its G-Max form (it cannot evolve,
          // so the target is always itself).
          { shiny: facts.isShiny, gender: facts.gender, gmax: facts.isGmax && potential.isCurrentForm },
        ),
        league: potential.league,
        leagues: potential.leagues,
        rank: potential.rank,
        name: potential.isCurrentForm ? null : potential.speciesName.toUpperCase(),
        percentText: potential.percent != null ? pct(potential.percent) : "",
        aura,
      };
    }
  }

  // The word version of the same story stays only while the visual cannot
  // render (no IVs yet): once the target block is up, repeating it in text
  // is exactly the noise the visual exists to remove.
  let evoLine: string | null = null;
  // BEFORE THE APPRAISAL (2026-09-04 owner direction: "stick to the same
  // format"): the card used to fall back to the packed grid layout (a
  // 36pt headline, no sprite) until the IVs were read, and it read as an
  // older HUD beside the appraised card. Now a species with a PvP standing
  // gets the split layout straight away: its own art as the visual, the
  // species' best league and rank on the ball, and the fact lines say what
  // is known (CP, HP) plus what the appraisal will add.
  //
  // EVERY species, not only the ranked ones (2026-09-28 owner direction:
  // "when there are no IVs on screen it should still show the Pokémon's
  // current sprite on the HUD"). A species outside the top-40 window used
  // to fall back to the packed text grid with no art at all; now the art
  // is the Pokémon AS SCANNED, in its form, with the ball and rank only
  // when a standing exists. The destiny picture waits for the IVs.
  let preAppraisal = false;
  if (target == null && facts.speciesId != null) {
    const standing = bestPvpStanding(facts.speciesId);
    const league = standing ? shortLeague(standing.league).toLowerCase() : "";
    const leagueId = (league === "gl" ? "great" : league === "ul" ? "ultra" : league === "ml" ? "master" : null) as
      | NonNullable<MyPokemonPipSlots["target"]>["league"]
      | null;
    preAppraisal = true;
    targetSpeciesId = facts.speciesId;
    target = {
      spriteUrl: hudArtworkUrl(facts.speciesId, facts.formId, {
        shiny: facts.isShiny,
        gender: facts.gender,
        gmax: facts.isGmax,
      }),
      league: leagueId,
      leagues: leagueId ? [leagueId] : [],
      rank: standing && leagueId ? standing.rank : null,
      name: standing?.evolveInto ? standing.evolveInto.toUpperCase() : null,
      percentText: "",
      aura,
    };
    if (standing && !leagueId) {
      evoLine = standing.evolveInto
        ? `EVOLVE → ${standing.evolveInto.toUpperCase()} · #${standing.rank} ${shortLeague(standing.league)}`
        : `TOP #${standing.rank} ${shortLeague(standing.league)} PICK`;
    }
  }

  // SPLIT-LAYOUT LEFT COLUMN.
  //
  // 2026-09-02, owner: "less is more". This used to carry a line per league —
  // GL, UL and ML, each with its own percent and full rank — and the target
  // block then repeated a percent and an oversized rank of its own. Four
  // numbers competing to be the answer to one question, and on the species
  // that cannot reach a cap three of them were the same number anyway.
  //
  // The pick already knows the best league (bestPotentialFor orders on percent
  // then rank), and the HUD already draws that league as a BALL. A ball says
  // "Great League" better than the letters "GL" do, so the words go and the
  // one percent and rank the pick was actually made on ride under the ball at
  // a size worth reading. What is left here is only what the ball cannot say:
  // how strong the Pokémon is now (CP) and what it was born with (the IVs).
  //
  // 2026-09-03: the IV line is the LAST entry on purpose. Both renderers draw
  // the last line at hero size, and the IVs are what the owner asked to make
  // prominent; CP sits above it, directly under the name.
  const heroLines: string[] = [];
  const little = littleLeagueCallout(facts);
  if (target != null && preAppraisal) {
    // SAME SHAPE AS THE APPRAISED CARD (2026-09-28 owner direction: "make
    // sure HUD formatting remains consistent on non-IV and IV scans; there
    // are size changes"). The appraised card is name / CP / the IV line at
    // hero size. Before the appraisal it used to be name / CP / HP / a long
    // "Appraise to add IVs" line fitted down to the column, so the rows and
    // type sizes jumped the moment the bars were read. Now the IV line is
    // drawn as its placeholder, "?/?/?", at the same hero size in the same
    // slot; the status line already says "Appraise it to add IVs", and HP
    // (which the appraised card never shows) stands down. A frame with no
    // CP yet (page scrolled to the stats) keeps the placeholder alone, so
    // the layout never drops back to the text grid mid-scan.
    if (facts.cp != null) heroLines.push(cpLine(facts));
    heroLines.push(IV_PLACEHOLDER_LINE);
  } else if (target != null) {
    if (facts.cp != null) heroLines.push(cpLine(facts));
    // The Little League call-out rides between CP and the IV line (2026-10-12
    // owner ask): the IV line stays LAST so both renderers keep drawing it at
    // hero size, and the destiny picture on the right is untouched.
    if (little) heroLines.push(little.line);
    if (facts.ivAtk != null && facts.ivDef != null && facts.ivHp != null) {
      // The raw IV percent rides WITH the triple it is the average of, which is
      // the only place it means what it says.
      heroLines.push(
        `${facts.ivAtk}/${facts.ivDef}/${facts.ivHp}` +
          (facts.ivPercent != null ? ` · ${pct(facts.ivPercent)}` : ""),
      );
    }
  }

  // MOVE PILLS (2026-09-03 owner ask): the moves the page read, as pills
  // under the fact lines. Only with the target up (the classic layout still
  // carries them in its grid row); fast move first, as the page lists them.
  const movePills =
    target != null ? facts.moves.slice(0, 3).map((m) => clipPill(m.name)) : [];
  // Each pill's type colour, index for index ("" when the move is unknown);
  // computed from the UNCLIPPED name (2026-09-30).
  const movePillColors =
    target != null ? facts.moves.slice(0, 3).map((m) => moveTypeHex(m.name)) : [];

  // The ranked moveset for the target form in the target league. PvPoke
  // publishes one moveset per species per league; the non-shadow row is the
  // one this scanner can vouch for.
  const moveRecs =
    target != null && target.league != null && targetSpeciesId != null
      ? rankedMovesFor(targetSpeciesId, target.league).slice(0, 3).map(clipPill)
      : [];

  return {
    headline,
    slots,
    leagueBadge: badge?.league ?? null,
    // The split layout owns the panel when heroLines ride with the target;
    // the hero pvp line would repeat the same percents, so it stands down.
    pvpLine: heroLines.length > 0 ? null : pvpLine,
    evoLine,
    target,
    heroLines,
    littleLeague: little && !preAppraisal && target != null ? { rank: little.rank, percentile: little.percentile } : null,
    movePills,
    movePillColors,
    moveRecs,
    hudFocus: facts.view === "appraisal" ? "ivs" : "moves",
    ivPercent: target != null && !preAppraisal ? facts.ivPercent : null,
  };
}
